import React, { forwardRef, useCallback, useRef, useState } from "react";
import "./MelaButton.css";
import { MelaTooltip } from "../MelaTooltip";
import type { MelaTooltipProps } from "../MelaTooltip";

export type MelaButtonVariant = "solid" | "soft" | "ghost" | "outline";
export type MelaButtonTone =
  | "neutral"
  | "primary"
  | "success"
  | "warning"
  | "danger"
  | "ai";
export type MelaButtonSize = "xxs" | "xs" | "sm" | "md" | "lg";
export type MelaButtonLoadingPosition = "start" | "end" | "center";

export interface MelaButtonProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    "children" | "onClick"
  > {
  children?: React.ReactNode;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void | Promise<void>;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  loading?: boolean;
  loadingPosition?: MelaButtonLoadingPosition;
  fullWidth?: boolean;
  iconOnly?: boolean;
  size?: MelaButtonSize;
  variant?: MelaButtonVariant;
  tone?: MelaButtonTone;
  dataTestId?: string;
  tooltip?: React.ReactNode;
  tooltipProps?: Omit<MelaTooltipProps, "children" | "content">;
}

const DEFAULT_VARIANT: MelaButtonVariant = "soft";
const DEFAULT_TONE: MelaButtonTone = "primary";
const DEFAULT_SIZE: MelaButtonSize = "md";

const getButtonClassName = ({
  className,
  size,
  variant,
  tone,
  fullWidth,
  isIconOnly,
}: {
  className: string;
  size: MelaButtonSize;
  variant: MelaButtonVariant;
  tone: MelaButtonTone;
  fullWidth: boolean;
  isIconOnly: boolean;
}) => {
  return [
    className,
    "mela-button",
    `mela-button--size-${size}`,
    `mela-button--variant-${variant}`,
    `mela-button--tone-${tone}`,
    fullWidth ? "mela-button--full-width" : "",
    isIconOnly ? "mela-button--icon-only" : "",
  ]
    .filter(Boolean)
    .join(" ");
};

export const MelaButton = forwardRef<HTMLButtonElement, MelaButtonProps>(
  (
    {
      children,
      startIcon,
      endIcon,
      loading = false,
      loadingPosition = "start",
      fullWidth = false,
      iconOnly = false,
      size = DEFAULT_SIZE,
      variant = DEFAULT_VARIANT,
      tone = DEFAULT_TONE,
      className = "",
      disabled = false,
      type = "button",
      dataTestId,
      tooltip,
      tooltipProps,
      style,
      onClick,
      ...props
    },
    ref,
  ) => {
    const isProcessingRef = useRef(false);
    const [isInternalProcessing, setIsInternalProcessing] = useState(false);

    const isLoading = loading || isInternalProcessing;
    const isDisabled = disabled || isLoading;

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (disabled || loading || isProcessingRef.current) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        if (!onClick) {
          return;
        }

        try {
          isProcessingRef.current = true;

          const result = onClick(event);

          if (result && typeof (result as Promise<void>).then === "function") {
            setIsInternalProcessing(true);

            result.finally(() => {
              isProcessingRef.current = false;
              setIsInternalProcessing(false);
            });

            return;
          }

          isProcessingRef.current = false;
        } catch (error) {
          isProcessingRef.current = false;
          setIsInternalProcessing(false);
          throw error;
        }
      },
      [onClick, disabled, loading],
    );

    const buttonContent = children;
    const isIconOnly = Boolean(
      iconOnly || (!buttonContent && (startIcon || endIcon)),
    );
    const spinnerPosition = isIconOnly ? "center" : loadingPosition;

    const showStartSpinner = isLoading && spinnerPosition === "start";
    const showEndSpinner = isLoading && spinnerPosition === "end";
    const showCenterSpinner = isLoading && spinnerPosition === "center";
    const iconNode = startIcon || endIcon;
    const buttonClassName = getButtonClassName({
      className,
      size,
      variant,
      tone,
      fullWidth,
      isIconOnly,
    });

    const iconOnlyContent =
      !showCenterSpinner && iconNode ? (
        <span className="mela-button__icon-slot">{iconNode}</span>
      ) : null;

    const textButtonContent = (
      <>
        {showStartSpinner ? (
          <span className="mela-button__icon-slot">
            <span className="mela-button__spinner" />
          </span>
        ) : startIcon ? (
          <span className="mela-button__icon-slot">{startIcon}</span>
        ) : null}
        {buttonContent ? (
          <span className="mela-button__label">{buttonContent}</span>
        ) : null}
        {showEndSpinner ? (
          <span className="mela-button__icon-slot">
            <span className="mela-button__spinner" />
          </span>
        ) : endIcon ? (
          <span className="mela-button__icon-slot">{endIcon}</span>
        ) : null}
      </>
    );

    const buttonNode = (
      <button
        {...props}
        ref={ref}
        type={type}
        className={buttonClassName}
        disabled={isDisabled}
        onClick={handleClick}
        data-testid={dataTestId}
        data-mela-button="true"
        aria-busy={isLoading || undefined}
        style={style}
      >
        {showCenterSpinner ? (
          <span className="mela-button__center-spinner">
            <span className="mela-button__spinner" />
          </span>
        ) : null}
        <span
          className={`mela-button__inner${showCenterSpinner ? " mela-button__inner--hidden" : ""}`}
        >
          {isIconOnly ? iconOnlyContent : textButtonContent}
        </span>
      </button>
    );

    if (!tooltip || tooltipProps?.disabled) {
      return buttonNode;
    }

    return (
      <MelaTooltip content={tooltip} fullWidth={fullWidth} {...tooltipProps}>
        {buttonNode}
      </MelaTooltip>
    );
  },
);

MelaButton.displayName = "MelaButton";
